import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useAssignDealerTemplate, useDealerTemplateAssignments, useDealerTemplateOptions, useRemoveDealerAssignment } from "../../../lib/api";
import { PrimaryButton, Select } from "../primitives";
import { focusRing } from "./ui";
import { placeholderNumbers } from "./templateFormat";

// Gives a dealer's template to one message step. Groups under that dealer then send it for that
// step once Meta has approved it; every other group keeps the brand's template.
export default function AssignTemplate({ row, tenantId, dealerName, onClose, onDone }) {
  const { data: options } = useDealerTemplateOptions();
  const { data: all } = useDealerTemplateAssignments(tenantId);
  const assign = useAssignDealerTemplate(tenantId);
  const remove = useRemoveDealerAssignment(tenantId);
  const [step, setStep] = useState("");
  const [fields, setFields] = useState({});
  const [error, setError] = useState("");
  const close = useRef(null);
  const numbers = placeholderNumbers(row.body);
  const steps = options?.steps || [];
  const label = (key) => steps.find((s) => s.key === key)?.label || key;
  const mine = (all || []).filter((a) => a.wa_template_request_id === row.id);

  useEffect(() => {
    close.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setError("");
    try {
      await assign.mutateAsync({ id: row.id, template_key: step, field_map: fields });
      onDone(label(step));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save. Try again in a moment.");
    }
  };
  const ready = step && numbers.every((n) => fields[n]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="tpl-assign-title" className="max-h-[90vh] w-full max-w-[480px] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h3 id="tpl-assign-title" className="m-0 text-[17px] font-semibold text-[#111827]">
            Use for a message step
          </h3>
          <button ref={close} type="button" onClick={onClose} aria-label="Close" className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB] ${focusRing}`}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <p className="mb-0 mt-3 text-[14.5px] leading-6 text-[#4B5563]">
          {dealerName ? (
            <>
              Groups under <strong>{dealerName}</strong> will send this template for the step you choose. Other groups keep the brand's template.
            </>
          ) : (
            <>The whole brand will send this template for the step you choose, once Meta has approved it. Until then the step keeps the template it has now.</>
          )}
        </p>

        {mine.length > 0 && (
          <ul className="m-0 mt-4 list-none space-y-2 p-0">
            {mine.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 rounded-xl bg-[#F9FAFB] px-4 py-3 text-[14px] text-[#111827]">
                <span>Used for {label(a.template_key)}</span>
                <button type="button" onClick={() => remove.mutate(a.id)} className={`cursor-pointer rounded-full px-3 py-1 text-[13px] font-medium text-[#B91C1C] hover:bg-[#FEE2E2] ${focusRing}`}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 space-y-4">
          <label className="block text-[13.5px] font-semibold text-[#374151]">
            Message step
            <Select aria-label="Message step" value={step} onChange={(e) => setStep(e.target.value)} className="mt-2 w-full">
              <option value="">Choose a step</option>
              {steps.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </Select>
          </label>
          {numbers.map((n) => (
            <label key={n} className="block text-[13.5px] font-semibold text-[#374151]">
              {`{{${n}}}`} is filled with
              <Select aria-label={`Fills {{${n}}}`} value={fields[n] || ""} onChange={(e) => setFields((f) => ({ ...f, [n]: e.target.value }))} className="mt-2 w-full">
                <option value="">Choose a field</option>
                {(options?.fields || []).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </label>
          ))}
        </div>
        {error && (
          <p role="alert" className="mb-0 mt-4 rounded-xl bg-[#FEF2F2] px-4 py-3 text-[14px] text-[#991B1B]">
            {error}
          </p>
        )}
        <div className="mt-6 flex justify-end">
          <PrimaryButton onClick={save} disabled={!ready || assign.isPending}>
            {assign.isPending ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
